import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";

// Tout le cache hors ligne vit dans Directory.Data (survit aux mises a jour
// de bundle OTA, contrairement a Cache que l'OS peut purger sous pression
// memoire). Fonctionne identiquement sur le web (le plugin stocke alors
// dans IndexedDB) et sur l'app - seule la lecture d'image (§getLocalImageSrc)
// differe reellement entre les deux, cf. plus bas.
const DIR = Directory.Data;
const MANIFEST_PATH = "offline/manifest.json";

const MIME_BY_EXT = {
  ".webp": "image/webp",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
};

// Manifeste en memoire : une grille peut afficher 200+ tuiles d'un coup,
// un Filesystem.stat() async par image serait trop lent. On charge une
// fois au demarrage la liste des cles deja en cache (initOfflineStore),
// et chaque ecriture met a jour ce Set + reprogramme une sauvegarde
// (debounce) plutot que de reecrire le fichier a chaque image.
let manifestImages = new Set();
let manifestSets = new Set();
// Sets ayant eu leur passage "haute qualité" complet (étape 3, à la demande
// depuis les Réglages) — distinct de manifestSets, qui ne dit que "JSON en
// cache" et ne suffit pas à savoir si les images HQ ont déjà été tentées.
let manifestHQSets = new Set();
let manifestLoaded = false;
let persistTimer = null;

export async function initOfflineStore() {
  if (manifestLoaded) return;
  manifestLoaded = true;
  try {
    const res = await Filesystem.readFile({ path: MANIFEST_PATH, directory: DIR, encoding: Encoding.UTF8 });
    const data = JSON.parse(res.data);
    manifestImages = new Set(data.images || []);
    manifestSets = new Set(data.sets || []);
    manifestHQSets = new Set(data.hqSets || []);
  } catch (e) {
    // Premier lancement : pas encore de manifeste, cache local vide.
  }
  registerFlushOnBackground();
  // Auto-guerison : le manifeste est ecrit avec un debounce (voir plus bas),
  // donc une fermeture d'appli juste apres une synchro peut avoir perdu les
  // toutes dernieres entrees alors que les FICHIERS, eux, sont bien sur le
  // disque (ecrits avant l'ajout au manifeste). Sans ca, ces images
  // deviennent invisibles pour toujours (hasLocalImage les ignore) meme si
  // elles sont bel et bien telechargees - constate en usage reel : des
  // cartes different a chaque ouverture. Tourne en tache de fond, ne bloque
  // jamais le premier rendu ; ne fait qu'AJOUTER des entrees, jamais en
  // retirer, donc sans risque d'ecraser un etat deja correct.
  reconcileManifestFromDisk();
}

async function persistManifestNow() {
  try {
    await Filesystem.writeFile({
      path: MANIFEST_PATH,
      data: JSON.stringify({
        images: [...manifestImages], sets: [...manifestSets], hqSets: [...manifestHQSets],
      }),
      directory: DIR,
      encoding: Encoding.UTF8,
      recursive: true,
    });
  } catch (e) {
    // Le manifeste n'est qu'un index de confort (les fichiers eux-memes
    // restent la source de verite) : une ecriture ratee n'est pas fatale,
    // on retentera a la prochaine modification.
  }
}

function schedulePersistManifest() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistManifestNow();
  }, 1500);
}

/** Force l'ecriture immediate du manifeste, en annulant le debounce en
 * attente. A appeler sur les points de coupure naturels (app mise en
 * arriere-plan, fin d'une synchro) pour ne jamais perdre les entrees des
 * 1,5 dernieres secondes si l'appli est tuee juste apres. */
export async function flushManifestNow() {
  if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
  await persistManifestNow();
}

function registerFlushOnBackground() {
  if (typeof document === "undefined") return;
  // "visibilitychange" se declenche de facon fiable a la fois sur le web ET
  // dans la WebView Android quand l'appli passe en arriere-plan (bouton
  // Accueil, changement d'appli) - pas besoin du plugin @capacitor/app pour
  // ca. "pagehide" couvre en plus une fermeture franche.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) flushManifestNow();
  });
  window.addEventListener("pagehide", () => { flushManifestNow(); });
}

async function listDir(path) {
  try {
    const res = await Filesystem.readdir({ path, directory: DIR });
    return res.files || [];
  } catch (e) {
    return [];
  }
}

async function reconcileManifestFromDisk() {
  try {
    let addedSets = false;
    for (const entry of await listDir("sets")) {
      const name = entry.name ?? entry;
      if (typeof name === "string" && name.endsWith(".json")) {
        const setId = name.slice(0, -".json".length);
        if (!manifestSets.has(setId)) { manifestSets.add(setId); addedSets = true; }
      }
    }

    let addedImages = false;
    for (const setEntry of await listDir("images")) {
      if (setEntry.type && setEntry.type !== "directory") continue;
      const setId = setEntry.name ?? setEntry;
      for (const fileEntry of await listDir(`images/${setId}`)) {
        if (fileEntry.type && fileEntry.type === "directory") continue;
        const fileName = fileEntry.name ?? fileEntry;
        const path = `images/${setId}/${fileName}`;
        if (!manifestImages.has(path)) { manifestImages.add(path); addedImages = true; }
      }
    }

    if (addedSets || addedImages) schedulePersistManifest();
  } catch (e) {
    // Best-effort : si le listage echoue (plateforme, permissions...), on
    // garde simplement le manifeste tel quel, comme avant cette fonction.
  }
}

// --- Donnees JSON (sets, liste des series, statut de collection) ---

export async function readJson(path) {
  try {
    const res = await Filesystem.readFile({ path, directory: DIR, encoding: Encoding.UTF8 });
    return JSON.parse(res.data);
  } catch (e) {
    return null;
  }
}

export async function writeJson(path, value) {
  try {
    await Filesystem.writeFile({
      path, data: JSON.stringify(value), directory: DIR, encoding: Encoding.UTF8, recursive: true,
    });
    if (path.startsWith("sets/") && path.endsWith(".json")) {
      manifestSets.add(path.slice("sets/".length, -".json".length));
      schedulePersistManifest();
    }
    return true;
  } catch (e) {
    // Ecriture best-effort : stockage plein/indisponible -> on continue
    // avec les donnees reseau deja affichees, pas de crash.
    return false;
  }
}

export function hasLocalSet(setId) {
  return manifestSets.has(setId);
}

export function localSetCount() {
  return manifestSets.size;
}

// Passage "haute qualité" (étape 3) : distinct de hasLocalSet ci-dessus, qui
// ne dit que "JSON en cache" — sans ce marqueur séparé, la synchro HQ
// devrait retaper /sets/{id} en réseau sur tous les sets à chaque clic, même
// une fois déjà faite (hasLocalSet serait déjà vrai pour la quasi-totalité
// des sets via la synchro "low" ou la simple navigation).
export function hasLocalHQSet(setId) {
  return manifestHQSets.has(setId);
}

export function markLocalHQSet(setId) {
  if (manifestHQSets.has(setId)) return;
  manifestHQSets.add(setId);
  schedulePersistManifest();
}

// --- Images ---

function extOf(path) {
  const m = /\.[a-z0-9]+$/i.exec(path);
  return m ? m[0].toLowerCase() : ".webp";
}

function sanitize(s) {
  return String(s).replace(/\//g, "_");
}

// Meme regle de nommage que backend/download_images.py et la route nginx
// /images/<set>/<carte>.<ext> : garde les deux chemins reconstructibles
// l'un a partir de l'autre sans appel reseau supplementaire.
export function imagePath(setId, cardId, ext) {
  return `images/${sanitize(setId)}/${sanitize(cardId)}${ext}`;
}

// Logo/symbole de set : meme dossier que les cartes du set (`_` en tete du
// nom de fichier pour ne jamais entrer en collision avec un id de carte,
// qui n'en commence jamais par un).
export function assetPath(setId, kind, ext) {
  return `images/${sanitize(setId)}/_${kind}${ext}`;
}

export function hasLocalImage(path) {
  return manifestImages.has(path);
}

export function cachedImageCount() {
  return manifestImages.size;
}

// Compte séparément les images "standard" et "haute qualité" (étape 3) : le
// manifeste est un seul Set partagé par les deux qualités (mêmes cartes,
// fichiers différents distingués par le suffixe "-hd" posé par
// cardLocalImagePath), donc un simple .size mélangerait les deux progressions
// une fois la synchro HQ utilisée. Les cartes `image_full` (Pokécardex/EN,
// un seul fichier possible, sans suffixe) sont comptées côté "low" — legere
// imprecision acceptee plutot qu'une precision parfaite ici.
const HQ_FILE_RE = /-hd\.[a-z0-9]+$/i;
export function cachedImageCountByQuality(quality) {
  let n = 0;
  for (const p of manifestImages) {
    const isHQFile = HQ_FILE_RE.test(p);
    if (quality === "high" ? isHQFile : !isHQFile) n++;
  }
  return n;
}

export async function writeImageFromUrl(path, url) {
  if (manifestImages.has(path)) return true;
  try {
    // Meme raison que le timeout axios de lib/api.js : fetch() n'a pas de
    // limite de temps par defaut, et un reseau mort (avion, zone blanche)
    // peut le laisser en attente indefiniment plutot que de rejeter vite.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    const resp = await fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
    if (!resp.ok) return false;
    const buf = await resp.arrayBuffer();
    await Filesystem.writeFile({ path, data: arrayBufferToBase64(buf), directory: DIR, recursive: true });
    manifestImages.add(path);
    schedulePersistManifest();
    return true;
  } catch (e) {
    return false;
  }
}

// Sur l'app (WebView native), Filesystem.getUri()+convertFileSrc() donne une
// URI utilisable directement dans un <img src>, sans charger les octets en
// JS. Sur le web, le plugin n'a pas de vrai file:// (stockage IndexedDB) :
// convertFileSrc n'y donnerait rien de chargeable, donc on relit les octets
// et on construit une data URL - c'est la seule vraie difference de
// plateforme de ce module.
export async function getLocalImageSrc(path) {
  if (!manifestImages.has(path)) return null;
  try {
    if (Capacitor.isNativePlatform()) {
      const { uri } = await Filesystem.getUri({ path, directory: DIR });
      return Capacitor.convertFileSrc(uri);
    }
    const res = await Filesystem.readFile({ path, directory: DIR });
    const mime = MIME_BY_EXT[extOf(path)] || "application/octet-stream";
    return `data:${mime};base64,${res.data}`;
  } catch (e) {
    return null;
  }
}

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Diagnostic (bouton "Tester le stockage" des Réglages) : écrit puis relit
// un petit fichier, SANS avaler l'erreur — contrairement à readJson/writeJson
// qui restent volontairement silencieux pour ne jamais faire planter l'app,
// ici on veut justement voir le message d'erreur natif exact si Filesystem
// échoue sur l'appareil, pour diagnostiquer sans accès à un débogueur.
export async function testOfflineStorage() {
  const marker = { t: Date.now() };
  const path = "offline/_test.json";
  try {
    await Filesystem.writeFile({
      path, data: JSON.stringify(marker), directory: DIR, encoding: Encoding.UTF8, recursive: true,
    });
  } catch (e) {
    return { ok: false, step: "écriture", error: e?.message || String(e) };
  }
  try {
    const res = await Filesystem.readFile({ path, directory: DIR, encoding: Encoding.UTF8 });
    const back = JSON.parse(res.data);
    if (back.t !== marker.t) return { ok: false, step: "lecture", error: "contenu différent de ce qui a été écrit" };
  } catch (e) {
    return { ok: false, step: "lecture", error: e?.message || String(e) };
  }
  try { await Filesystem.deleteFile({ path, directory: DIR }); } catch (e) {}
  return {
    ok: true,
    platform: Capacitor.isNativePlatform() ? "app (natif)" : "web",
    images: manifestImages.size,
    sets: manifestSets.size,
  };
}

// Fichiers JSON qui décrivent UN compte (pas le catalogue) : à purger quand
// un AUTRE compte se connecte sur le même appareil. Sans ça (bug réel du
// 2026-09-14) : si une requête échoue (timeout), withOfflineFallback ressert
// le dernier fichier écrit — qui peut être celui de l'utilisateur précédent.
// « 146 cartes possédées » affichées sur le compte VenusJ (9 400 cartes)
// étaient en fait celles du compte venus, connecté avant sur ce téléphone.
export const USER_CACHE_FILES = [
  "collection-status.json", "collection-stats.json", "collection-completion.json", "me.json",
];

export async function clearUserCache() {
  for (const path of USER_CACHE_FILES) {
    try { await Filesystem.deleteFile({ path, directory: DIR }); } catch (e) {}
  }
}

export async function clearOfflineCache() {
  for (const path of ["images", "sets"]) {
    try { await Filesystem.rmdir({ path, directory: DIR, recursive: true }); } catch (e) {}
  }
  for (const path of [
    "series-with-sets.json", "collection-status.json", "collection-stats.json",
    "collection-completion.json", "me.json", MANIFEST_PATH,
    // PAS "offline/write-queue.json" : vider le cache de LECTURE ne doit
    // jamais faire perdre des écritures pas encore synchronisées avec le
    // serveur (cf. étape 3, hooks/useCollection.js).
  ]) {
    try { await Filesystem.deleteFile({ path, directory: DIR }); } catch (e) {}
  }
  manifestImages = new Set();
  manifestSets = new Set();
  manifestHQSets = new Set();
}
